import React, { useMemo } from 'react'
import { PositionDetails } from 'types/position'
import { FeeAmount, Position } from '@uniswap/v3-sdk'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { usePool } from 'hooks/usePools'
import { useToken } from 'hooks/Tokens'
import { useHistory } from 'react-router-dom'
import { WETH9, Price, Token, Percent, CurrencyAmount, Fraction } from '@uniswap/sdk-core'
import { formatPrice, formatTokenAmount } from 'utils/formatTokenAmount'
import Loader from 'components/Loader'
import { unwrappedToken } from 'utils/wrappedCurrency'
import { DAI, USDC, USDT, WBTC } from '../../../constants'

import QuestionHelper from 'components/QuestionHelper'
import Tag from 'components/Tag'
import { PositionListCardContainer, PositionListCardPriceContainer } from './styled'
import TagRange from './TagRange'
import TagButton from 'components/TagButton'
import axios from 'axios'
import { API_POOL_BASE_URL } from 'constants/links'
import { roundToDecimalPlace } from 'utils/data'

import CurrencyLogo from 'components/CurrencyLogo'
import { useV3PositionFees } from 'hooks/useV3PositionFees'
import useUSDCPrice from 'hooks/useUSDCPrice'
import { TYPE } from 'theme'

export function getPriceOrderingFromPositionForUI(
  position?: Position
): {
  priceLower?: Price
  priceUpper?: Price
  quote?: Token
  base?: Token
} {
  if (!position) {
    return {}
  }

  const token0 = position.amount0.token
  const token1 = position.amount1.token

  // if token0 is a dollar-stable asset, set it as the quote token
  const stables = [DAI, USDC, USDT]
  if (stables.some((stable) => stable.equals(token0))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if token1 is an ETH-/BTC-stable asset, set it as the base token
  const bases = [...Object.values(WETH9), WBTC]
  if (bases.some((base) => base.equals(token1))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if both prices are below 1, invert
  if (position.token0PriceUpper.lessThan(1)) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // otherwise, just return the default
  return {
    priceLower: position.token0PriceLower,
    priceUpper: position.token0PriceUpper,
    quote: token1,
    base: token0,
  }
}

export interface PositionListItemProps {
  positionDetails: PositionDetails
  inRange?: boolean
  outOfRange?: boolean
  inactive?: boolean
}
interface ApiPoolData {
  apr: string
  display: boolean
  fee: number
  h1: string
  h6: string
  h24: string
  pairAdress: string
  prevH24: string
  reserves: any
  totalTvlUSD: string
  version: string
  token0: {
    token0Adress: string
    token0Name: string
    token0Symbol: string
  }
  token1: {
    token1Adress: string
    token1Name: string
    token1Symbol: string
  }
}
function customFormatUpperPrice(price: Price | undefined, sigFigs: number) {
  if (!price) {
    return '-'
  }
  if (parseFloat(price.toFixed(sigFigs)) > Math.pow(10, 7)) {
    return '+9 999 999'
  }

  if (parseFloat(price.toFixed(sigFigs)) < 0.0000000000000000000000000000001) {
    return '≈0'
  }
  return price.toSignificant(sigFigs)
}
function getRatio(lower: Price, current: Price, upper: Price) {
  try {
    if (!current.greaterThan(lower)) {
      return 100
    } else if (!current.lessThan(upper)) {
      return 0
    }

    const a = Number.parseFloat(lower.toSignificant(15))
    const b = Number.parseFloat(upper.toSignificant(15))
    const c = Number.parseFloat(current.toSignificant(15))

    const ratio = Math.floor((1 / ((Math.sqrt(a * b) - Math.sqrt(b * c)) / (c - Math.sqrt(b * c)) + 1)) * 100)

    if (ratio < 0 || ratio > 100) {
      console.warn('Out of range')
    }

    return ratio
  } catch {
    return undefined
  }
}

const TagId: React.FC<{ positionDetails: any }> = ({ positionDetails }) => {
  return (
    <div style={{ position: 'absolute', top: '0', right: '0' }}>
      <Tag variant={1}>ID: {positionDetails?.tokenId.toString()}</Tag>
    </div>
  )
}
const PositionListItem = ({ positionDetails, inRange, outOfRange, inactive }: PositionListItemProps) => {
  const {
    token0: token0Address,
    token1: token1Address,
    fee: feeAmount,
    liquidity,
    tickLower,
    tickUpper,
  } = positionDetails

  const history = useHistory()
  const token0 = useToken(token0Address)
  const token1 = useToken(token1Address)

  const currency0 = token0 ? unwrappedToken(token0) : undefined
  const currency1 = token1 ? unwrappedToken(token1) : undefined

  // construct Position from details returned
  const [, pool] = usePool(currency0 ?? undefined, currency1 ?? undefined, feeAmount)
  const [loading, setLoading] = React.useState<boolean>(true)
  const [apiData, setApiData] = React.useState<null | ApiPoolData>(null)
  const [feeValue0, feeValue1] = useV3PositionFees(pool ?? undefined, positionDetails)
  const position = useMemo(() => {
    if (pool) {
      return new Position({ pool, liquidity: liquidity.toString(), tickLower, tickUpper })
    }
    return undefined
  }, [liquidity, pool, tickLower, tickUpper])

  const price0 = useUSDCPrice(token0 ?? undefined)
  const price1 = useUSDCPrice(token1 ?? undefined)

  const fiatValueOfFees: CurrencyAmount | null = useMemo(() => {
    if (!price0 || !price1 || !feeValue0 || !feeValue1) return null
    const amount0 = price0.quote(feeValue0)
    const amount1 = price1.quote(feeValue1)
    return amount0.add(amount1)
  }, [price0, price1, feeValue0, feeValue1])

  const fiatValueOfLiquidity: CurrencyAmount | null = useMemo(() => {
    if (!price0 || !price1 || !position) return null
    const amount0 = price0.quote(position.amount0)
    const amount1 = price1.quote(position.amount1)
    return amount0.add(amount1)
  }, [price0, price1, position])

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true)

      try {
        const response = await axios.get(`${API_POOL_BASE_URL}/api/pairs`, {
          params: {
            version: 'v3',
            token0: token0?.address,
            token1: token1?.address,
            fee: feeAmount,
          },
        })

        if (response.data?.data?.pairs?.length > 0) {
          setApiData(response.data?.data?.pairs[0])
        } else {
          const reversedResponse = await axios.get(`${API_POOL_BASE_URL}/api/pairs`, {
            params: {
              version: 'v3',
              token0: token1?.address,
              token1: token0?.address,
              fee: feeAmount,
            },
          })
          if (reversedResponse.data?.data?.pairs?.length > 0) {
            setApiData(reversedResponse.data?.data?.pairs[0])
          }
        }

        setLoading(false)
      } catch (e) {
        console.warn(e)
        setLoading(false)
      }
    }

    if (loading && token0 && token1) {
      fetchData()
    }
  }, [feeAmount, loading, token0, token1])

  // prices
  const { priceLower, priceUpper, base } = getPriceOrderingFromPositionForUI(position)
  const inverted = token1 ? base?.equals(token1) : undefined
  const currencyQuote = inverted ? currency1 : currency0
  const currencyBase = inverted ? currency0 : currency1

  // check if price is within range
  const isOutOfRange: boolean = pool ? pool.tickCurrent < tickLower || pool.tickCurrent >= tickUpper : false

  const ratio = useMemo(() => {
    return priceLower && pool && priceUpper
      ? getRatio(
          inverted ? priceUpper.invert() : priceLower,
          pool.token0Price,
          inverted ? priceLower.invert() : priceUpper
        )
      : undefined
  }, [inverted, pool, priceLower, priceUpper])

  const removed = liquidity?.eq(0)

  const shouldDisplay =
    (inRange && !removed && !isOutOfRange) || (outOfRange && !removed && isOutOfRange) || (inactive && removed)
  if (!shouldDisplay) {
    return null
  }

  /* Custom warning */
  const displayWarning = token0?.symbol === 'WHYPE' && token1?.symbol === 'LHYPE' && feeAmount === FeeAmount.MEDIUM

  const viewRoute = '/pool/' + positionDetails.tokenId
  const addRoute = `/increase/${currency0 instanceof Token ? currency0.address : 'HYPE'}/${
    currency1 instanceof Token ? currency1.address : 'HYPE'
  }/${feeAmount}/${positionDetails.tokenId}`

  const isVolumeIncreasing =
    apiData?.prevH24 && apiData?.h24 ? parseFloat(apiData?.h24) - parseFloat(apiData?.prevH24) > 0 : null

  const volumeIncreasingIcon =
    isVolumeIncreasing !== null ? (
      isVolumeIncreasing ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#4AFB39"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-trending-up"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M3 17l6 -6l4 4l8 -8" />
          <path d="M14 7l7 0l0 7" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="red"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-trending-down"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M3 7l6 6l4 -4l8 8" />
          <path d="M21 10l0 7l-7 0" />
        </svg>
      )
    ) : (
      <></>
    )

  return (
    <>
      <PositionListCardContainer>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: '22px',
            marginBottom: '12px',
            position: 'relative',
          }}
        >
          <DoubleCurrencyLogo currency0={currencyBase} currency1={currencyQuote} size={48} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: 'column',
              justifyContent: 'flex-start',
            }}
          >
            <span
              style={{
                fontWeight: 500,
                fontSize: 18,
                textAlign: 'left',
                marginRight: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {currencyQuote?.symbol} / {currencyBase?.symbol}
              {displayWarning && (
                <QuestionHelper
                  background={'#ff8f00'}
                  color={'white'}
                  text="For your information: A higher APR is available in the V3 0.1% pool."
                />
              )}
            </span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '8px',
                flexWrap: 'wrap',
                width: '100%',
              }}
            >
              <Tag variant={1}>{new Percent(feeAmount, 1_000_000).toSignificant()}%</Tag>

              <TagRange removed={removed} inRange={!isOutOfRange} />
            </div>
          </div>

          <TagId positionDetails={positionDetails} />
        </div>

        <div style={{ display: 'flex', gap: '32px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
            <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '12px' }}>APR</span>
            <span
              style={{
                fontWeight: 500,
                fontSize: '14px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
              }}
              className="responsive-text"
            >
              {apiData?.apr && parseFloat(apiData?.apr) > 50 && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="rgb(236, 159, 72)"
                  stroke="rgb(233, 66, 30)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-flame"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M12 10.941c2.333 -3.308 .167 -7.823 -1 -8.941c0 3.395 -2.235 5.299 -3.667 6.706c-1.43 1.408 -2.333 3.621 -2.333 5.588c0 3.704 3.134 6.706 7 6.706s7 -3.002 7 -6.706c0 -1.712 -1.232 -4.403 -2.333 -5.588c-2.084 3.353 -3.257 3.353 -4.667 2.235" />
                </svg>
              )}
              {apiData?.apr ? `${roundToDecimalPlace(`${apiData?.apr}`, '2')}%` : 'N/A'}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
            <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '12px' }}>1D Volume</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '2px',
              }}
            >
              {volumeIncreasingIcon && volumeIncreasingIcon}{' '}
              <span style={{ fontWeight: 500, fontSize: '14px' }} className="responsive-text">
                {apiData?.h24 ? `$${roundToDecimalPlace(`${apiData?.h24}`, '2')}` : 'N/A'}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
            <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '12px' }}>Total TVL</span>
            <span style={{ fontWeight: 500, fontSize: '14px' }} className="responsive-text">
              {apiData?.totalTvlUSD ? `$${roundToDecimalPlace(`${apiData?.totalTvlUSD}`, '2')}` : 'N/A'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
          <span
            style={{
              color: '#c3c5cb',
              fontWeight: 400,
              fontSize: '12px',
              display: 'flex',
              gap: '4px',
              alignItems: 'center',
            }}
          >
            Your Reserve{' '}
            <span style={{ display: 'flex' }}>
              (
              {fiatValueOfLiquidity?.greaterThan(new Fraction(1, 100)) ? (
                <TYPE.small fontSize="12px" fontWeight={500}>
                  ${fiatValueOfLiquidity.toFixed(2)}
                </TYPE.small>
              ) : (
                <TYPE.largeHeader fontSize="12px" fontWeight={500}>
                  $-
                </TYPE.largeHeader>
              )}
              )
            </span>
          </span>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CurrencyLogo currency={currencyQuote} size={'20px'} />
              <span style={{ fontSize: '12px', fontWeight: 500 }}>
                {roundToDecimalPlace(
                  `${inverted ? position?.amount1.toSignificant(4) : position?.amount0.toSignificant(4)}`,
                  '7'
                )}{' '}
                {currencyQuote?.symbol ? currencyQuote?.symbol : ''}
              </span>
              {ratio ? <Tag variant={1}>{inverted ? 100 - ratio : ratio}%</Tag> : ''}
            </div>
            <span style={{ padding: '0 4px' }}>/</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CurrencyLogo currency={currencyBase} size={'20px'} />
              <span style={{ fontSize: '12px', fontWeight: 500 }}>
                {roundToDecimalPlace(
                  `${inverted ? position?.amount0.toSignificant(4) : position?.amount1.toSignificant(4)}`,
                  '7'
                )}{' '}
                {currencyBase?.symbol ? currencyBase?.symbol : ''}
              </span>
              {ratio ? <Tag variant={1}>{inverted ? ratio : 100 - ratio}%</Tag> : ''}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
          <span
            style={{
              color: '#c3c5cb',
              fontWeight: 400,
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Unclaimed fees{' '}
            <span style={{ display: 'flex' }}>
              (
              {fiatValueOfFees?.greaterThan(new Fraction(1, 100)) ? (
                <TYPE.small fontSize="12px" fontWeight={500}>
                  ${fiatValueOfFees.toFixed(2)}
                </TYPE.small>
              ) : (
                <TYPE.small fontSize="12px" fontWeight={500}>
                  $-
                </TYPE.small>
              )}
              )
            </span>
          </span>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CurrencyLogo currency={currencyQuote} size={'20px'} />
              <span style={{ fontSize: '12px', fontWeight: 500 }}>
                {inverted
                  ? feeValue1
                    ? formatTokenAmount(feeValue1, 4)
                    : '-'
                  : feeValue0
                  ? formatTokenAmount(feeValue0, 4)
                  : '-'}{' '}
                {currencyQuote?.symbol ? currencyQuote?.symbol : ''}
              </span>
            </div>
            <span style={{ padding: '0 4px' }}>/</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CurrencyLogo currency={currencyBase} size={'20px'} />
              <span style={{ fontSize: '12px', fontWeight: 500 }}>
                {inverted
                  ? feeValue0
                    ? formatTokenAmount(feeValue0, 4)
                    : '-'
                  : feeValue1
                  ? formatTokenAmount(feeValue1, 4)
                  : '-'}{' '}
                {currencyBase?.symbol ? currencyBase?.symbol : ''}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '6px', alignItems: 'center' }}
        >
          <PositionListCardPriceContainer>
            {priceLower && priceUpper ? (
              <>
                <span style={{ fontSize: '12px', fontWeight: 500, color: 'rgb(195, 197, 203)' }}>Min Price</span>
                <span style={{ fontSize: '16px', color: 'white' }}>{formatPrice(priceLower, 4)}</span>
                <span style={{ fontSize: '12px', fontWeight: 500, color: 'rgb(195, 197, 203)' }}>
                  {currencyBase?.symbol} {' / '} {currencyQuote?.symbol}
                </span>
              </>
            ) : (
              <Loader></Loader>
            )}
          </PositionListCardPriceContainer>

          <PositionListCardPriceContainer>
            {priceLower && priceUpper ? (
              <>
                <span style={{ fontSize: '12px', fontWeight: 500, color: 'rgb(195, 197, 203)' }}>Max Price</span>
                <span style={{ fontSize: '16px', color: 'white' }}>{customFormatUpperPrice(priceUpper, 4)}</span>
                <span style={{ fontSize: '12px', fontWeight: 500, color: 'rgb(195, 197, 203)' }}>
                  {currencyBase?.symbol} {' / '} {currencyQuote?.symbol}
                </span>
              </>
            ) : (
              <Loader></Loader>
            )}
          </PositionListCardPriceContainer>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
          <TagButton
            onClick={() => {
              history.push(viewRoute)
            }}
            style={{ width: '100%', fontSize: '14px', padding: '10px', height: 'unset' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-edit"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
              <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
              <path d="M16 5l3 3" />
            </svg>
            Manage
          </TagButton>
          <TagButton
            onClick={() => {
              history.push(addRoute)
            }}
            style={{ width: '100%', fontSize: '14px', padding: '10px', height: 'unset' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-plus"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M12 5l0 14" />
              <path d="M5 12l14 0" />
            </svg>
            Add Liq.
          </TagButton>
        </div>
      </PositionListCardContainer>
    </>
  )
}
export default PositionListItem
